import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/dragdrop/x-drag-drop.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { CrDataWarning } from '@chancenwerk/client/components/chancen-component-data-warning.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { StudentType } from '@chancenwerk/enums/enums.js';
import { DurchfuehrungsTag } from '@chancenwerk/shared/_cms/products/entities/Product.js';
import type { ProductLKK } from '@chancenwerk/shared/_cms/products/entities/ProductLKK.js';
import { Course } from '@chancenwerk/shared/courses/Course.js';
import {
  CourseLernfoerderung,
  courseMatchesDurchfuehrungsTag,
  isCourseOfActiveGeneration,
} from '@chancenwerk/shared/courses/CourseLernfoerderung.js';
import { ChancenwerkUserFilter } from '@chancenwerk/shared/filter/ChancenwerkUserFilter.js';
import { ChancenRoleAspect } from '@chancenwerk/shared/semantics/ChancenRoleAspect.js';
import { Role } from '@chancenwerk/shared/semantics/Role.js';
import '@chancenwerk/client/design/components/cr-flex.js';
import type { ChromiumFlex } from '@chancenwerk/client/design/components/cr-flex.js';
import '@chancenwerk/client/design/components/cr-grid.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import '@chancenwerk/client/design/components/cr-progress-bar.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import { KPFAspect } from '@chancenwerk/shared/master-data-management/kpf/entities/aspects/KPFAspect.js';
import { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import { UserAPI } from '@chancenwerk/shared/master-data-management/users/api/UserAPI.js';
import { StudentTypeAspect } from '@chancenwerk/shared/master-data-management/users/entities/aspects/StudentTypeAspect.js';
import type { ChancenwerkUser } from '@chancenwerk/shared/master-data-management/users/entities/ChancenwerkUser.js';
import { ExternerMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/ExternerMitarbeiter.js';
import { Mitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/Mitarbeiter.js';
import { Student } from '@chancenwerk/shared/master-data-management/users/entities/Student.js';
import { VerwaltungsMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/VerwaltungsMitarbeiter.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, type Ref, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { map, switchMap, takeUntil, tap } from 'rxjs';
import '@chancenwerk/client/design/components/cr-nospace-toggle-content.js';
import '../components/board-component-course-aggregated-detail-card.js';
import '../components/board-component-course-detail-card.js';
import '../components/board-component-drag-drop.js';
import '../components/board-component-user-card.js';
import '../components/board-component-user-count-stats.js';
import '../components/board-component-user-pool-card.js';
import '../components/board-component-user-pool.js';
import { BoardDialogEditCourse, CourseProxy } from '../components/dialogs/board-dialog-edit-course.js';
import { dirtyData } from '../globalSubjects.js';
import { BoardRoutes } from '../routes.js';

const courseSelector: BaseQLSelectionSet<Course> = {
  _id: 1,
  _class: 1,
  kpfID: 1,
  location: 1,
  weekday: 1,
  startTime: 1,
  endTime: 1,
  leKoIDs: 1,
  osStudentIDs: 1,
  usStudentIDs: 1,
  schuKoIDs: 1,
  startDate: 1,
  plannedStartDate: 1,
  version: 1,
  schuKos: { _id: 1, _class: 1, vorname: 1, username: 1, nachname: 1, email: 1 },
  leKos: { _id: 1, _class: 1, vorname: 1, username: 1, nachname: 1, email: 1 },
  osStudents: {
    _id: 1,
    _class: 1,
    vorname: 1,
    nachname: 1,
    username: 1,
    guestAccount: 1,
    inactive: 1,
    email: 1,
    schuelertyp: 1,
    schoolInformation: { _class: 1, jahrgangsstufe: 1, zug: 1 },
    kpfs: { school: { name: 1 } },
    kuendigung: { austrittsdatum: 1 },
    lernfoerderungDuerchfuehrungsTagChoosen: DurchfuehrungsTag.allFields,
  },
  usStudents: {
    _id: 1,
    _class: 1,
    vorname: 1,
    nachname: 1,
    username: 1,
    guestAccount: 1,
    inactive: 1,
    email: 1,
    schuelertyp: 1,
    schoolInformation: { _class: 1, jahrgangsstufe: 1, zug: 1 },
    kpfs: { school: { name: 1 } },
    kuendigung: { austrittsdatum: 1 },
    lernfoerderungDuerchfuehrungsTagChoosen: DurchfuehrungsTag.allFields,
  },
};

const studentSelector: BaseQLSelectionSet<Student> = {
  _id: 1,
  _class: 1,
  vorname: 1,
  username: 1,
  inactive: 1,
  nachname: 1,
  email: 1,
  guestAccount: 1,
  schuelertyp: 1,
  schoolInformation: { _class: 1, jahrgangsstufe: 1, zug: 1 },
  kuendigung: { austrittsdatum: 1 },
  kpfs: { school: { name: 1 } },
  lernfoerderungDuerchfuehrungsTagChoosen: DurchfuehrungsTag.allFields,
};

const lekoSelector: BaseQLSelectionSet<Mitarbeiter> = {
  _id: 1,
  _class: 1,
  username: 1,
  vorname: 1,
  nachname: 1,
  employeeID: 1,
};

const kpfSelector: BaseQLSelectionSet<KPF<ProductLKK>> = {
  _id: 1,
  kpf: 1,
  schoolID: 1,
  school: { _class: 1, _id: 1, name: 1, kuerzel: 1 },
  schuKoIDs: 1,
  accessTokenForExternals: 1,
  accessTokenForStudents: 1,
  kuerzel: 1,
  schuKos: { _id: 1, _class: 1, vorname: 1, nachname: 1, email: 1, employeeID: 1 },
  leKos: { _id: 1, _class: 1 },
  produkt: {
    _class: 1,
    useV2Courses: 1,
    USLernFoerderung: 1,
    OSLernFoerderung: 1,
    uebungsLeiterProLernFoerderung: 1,
    kuerzel: 1,
    durchfuehrungsTage: DurchfuehrungsTag.allFields,
  },
};

/**
 * @customElement board-page-course-details-lernfoerderung
 */
@customElement('board-page-course-details-lernfoerderung')
export class BoardPageCourseDetails extends CrPage {
  override styles() {
    return [...super.styles(), { ':host': { height: '100%' } }];
  }

  constructor() {
    super();
    this.label = 'Lernförderung';
    this.backRoute = BoardRoutes.overview;
  }

  @property() public kpfID = new RXController(this, '');

  @state() private readonly _kpf = new RXController(
    this,
    this.kpfID.observable.pipe(switchMap(id => KPF.getByID<KPF<ProductLKK>>(id ?? '')(kpfSelector))),
  );

  @state() private readonly _coursesFromKPF = new RXController(
    this,
    this._kpf.observable.pipe(
      switchMap(kpf => Course.subscribeCoursesByKPFID([kpf?._id ?? ''])(courseSelector)),
      map(res => res?.filter(c => isCourseOfActiveGeneration(c, this._kpf.value?.produkt))),
      tap(data => dirtyData.next(data)),
    ),
  );

  @state() private readonly _courseMap = new RXController(
    this,
    this._coursesFromKPF.observable.pipe(
      map(courses => {
        const courseMap = new Map<string, Course[]>();

        // * With the new V2 courses, we don't need to group anymore. Everything is in one aggregated card.
        if (this._kpf.value?.produkt?.useV2Courses) {
          courseMap.set('V2', courses ?? []);
          return courseMap;
        }

        for (const course of courses ?? []) {
          const entry = courseMap.get(course.location);
          if (!entry) {
            courseMap.set(course.location, [course]);
          } else {
            courseMap.set(course.location, [...entry, course]);
          }
        }
        return courseMap;
      }),
    ),
  );

  @state() private readonly _kpfStudentsUS = new RXController(
    this,
    this._kpf.observable.pipe(
      switchMap(kpf =>
        UserAPI.subscribeByFilter(
          0,
          0,
          new ChancenwerkUserFilter({
            userClass: [Student._class],
            aspects: [
              new BooleanAspect({ customID: 'active', value: true }),
              new KPFAspect({ value: kpf?._id ?? '' }),
              new StudentTypeAspect({ value: StudentType.US }),
            ],
          }),
        )(studentSelector),
      ),
    ),
  );

  @state() private readonly _kpfStudentsOS = new RXController(
    this,
    this._kpf.observable.pipe(
      switchMap(kpf =>
        UserAPI.subscribeByFilter(
          0,
          0,
          new ChancenwerkUserFilter({
            userClass: [Student._class],
            aspects: [
              new BooleanAspect({ customID: 'active', value: true }),
              new KPFAspect({ value: kpf?._id ?? '' }),
              new StudentTypeAspect({ value: StudentType.OS }),
            ],
          }),
        )(studentSelector),
      ),
    ),
  );

  @state() private readonly _kpfLeKos = new RXController(
    this,
    this._kpf.observable.pipe(
      switchMap(kpf =>
        UserAPI.subscribeByFilter(
          0,
          0,
          new ChancenwerkUserFilter({
            userClass: [VerwaltungsMitarbeiter._class, Mitarbeiter._class, ExternerMitarbeiter._class],
            aspects: [
              new ChancenRoleAspect({ value: Role.LEKO }),
              new BooleanAspect({ customID: 'active', value: true }),
              new KPFAspect({ value: kpf?._id ?? '' }),
            ],
          }),
        )(lekoSelector),
      ),
    ),
  );

  private readonly _scroll: Ref<ChromiumFlex> = createRef();

  override connectedCallback(): void {
    super.connectedCallback();

    this._kpf.observable
      .pipe(
        takeUntil(this.disconnected),
        tap(kpf => {
          this.label = `Lernförderung ${kpf?.kuerzel ?? ''}`;
        }),
      )
      .subscribe();
  }

  override header() {
    return html`
      <cr-flex space="md" horizontal crossaxis-center>
        <board-component-user-count-stats
          ?loading=${this._coursesFromKPF.isLoading || this._courseMap.isLoading}
          .elements=${{
            SchuKos: this._kpf.value?.schuKos?.length ?? 0,
            LeKos: this._kpfLeKos.value
              ? html`<cr-text>${this._kpfLeKos.value.length}</cr-text>`
              : html`<cr-loader ${css({ height: '12px', width: '12px' })}></cr-loader>`,
            Jugendliche: this._kpfStudentsOS.value?.length ?? 0,
            Kinder: this._kpfStudentsUS.value?.length ?? 0,
          }}
        >
        </board-component-user-count-stats>
        <cr-button
          mode="action"
          @click=${async () => {
            await ChromiumDialog.waitFor(this._saveAll());
          }}
        >
          Speichern
        </cr-button>
      </cr-flex>
    `;
  }

  override content() {
    if (this._coursesFromKPF.isLoading || this._courseMap.isLoading)
      return html`
        <cr-flex center crossaxis-center>
          <cr-loader></cr-loader>
        </cr-flex>
      `;
    return html`
      <cr-flex space="sm" padding="xs" flex horizontal ${css({ minHeight: 0 })}>
        <cr-flex  ${ref(this._scroll)} space="md" ${css({
          flex: 4,
          overflowY: 'auto',
          paddingBottom: this.spacing.lg,
        })} >
          ${repeat(
            this._courseMap.value?.keys() ?? [],
            k => k,
            location => {
              const courses = this._courseMap.value?.get(location);
              return html`<board-component-course-aggregated-detail-card
                .coursesToManage=${courses}
                .otherCourses=${this._isV2()
                  ? []
                  : (this._coursesFromKPF.value ?? []).filter(c => !courses?.some(mc => mc._id === c._id))}
                .kpf=${this._kpf.value}
                @dropped=${() => this.requestUpdate()}
                @dropped-elsewhere=${() => this.requestUpdate()}
                @course-edited=${() => this.requestUpdate()}
              ></board-component-course-aggregated-detail-card>`;
            },
          )}
          <cr-card w100 ${css({
            background: this.colors.primary.shade(300),
            cursor: 'pointer',
          })} center crossaxis-center 
          @click=${async () => {
            if ((this._kpf.value?.produkt as ProductLKK).durchfuehrungsTage.length === 0) {
              await ChromiumSnackbar.show('Es sind keine Durchführungstage gesetzt!');
              return;
            }

            await BoardDialogEditCourse.showPopup({
              props: {
                kpf: this._kpf.value,
                courseProxy: new CourseProxy({
                  courses: (this._kpf.value?.produkt as ProductLKK).durchfuehrungsTage.map(
                    (t: DurchfuehrungsTag) =>
                      new CourseLernfoerderung({
                        kpfID: this._kpf.value?._id,
                        kpf: this._kpf.value,
                        weekday: t.wochentag,
                        startTime: t.startZeit,
                        endTime: t.endZeit,
                      }),
                  ),
                }),
                courseType: CourseLernfoerderung._class,
                new: true,
              },
            });
            this.requestUpdate();
          }}
          >
          <cr-tooltip> <cr-text> Kurs hinzufügen </cr-text> </cr-tooltip>
            <cr-icon ${css({ color: 'white', fontSize: '2rem' })}> add </c-icon>
          </cr-card>
        </cr-flex>

        <board-component-user-pool-card>

        <x-flex slot="overTitle" ${css({
          display: this._coursesFromKPF.value?.some(c => c.isDirty()) ? 'block' : 'none',
        })}>
          ${CrDataWarning(this._coursesFromKPF.value?.some(c => c.isDirty()))}
        </x-flex>

        <board-component-user-pool
          label="Kinder"
          .kpfID=${this.kpfID.value}
          .users=${this._filterPoolUsers(this._kpfStudentsUS.value ?? [], 'US')}
          .userType=${'US'}
          .courses=${this._coursesFromKPF.value ?? []}
          .showChoosenCourses=${true}
          @pool-changed=${() => this.requestUpdate()}
        ></board-component-user-pool>
        <board-component-user-pool
          label="Jugendliche"
          .kpfID=${this.kpfID.value}
          .users=${this._filterPoolUsers(this._kpfStudentsOS.value ?? [], 'OS')}
          .userType=${'OS'}
          .courses=${this._coursesFromKPF.value ?? []}
          .showChoosenCourses=${true}
          @pool-changed=${() => this.requestUpdate()}
        ></board-component-user-pool>
        <board-component-user-pool
          label="LeKos"
          .users=${this._filterPoolUsers(this._kpfLeKos.value ?? [], 'LeKo')}
          .userType=${'LeKo'}
          .courses=${this._coursesFromKPF.value ?? []}
          @pool-changed=${() => this.requestUpdate()}
        ></board-component-user-pool>
        <board-component-user-pool
          label="SchuKos"
          .users=${this._filterPoolUsers(this._kpf.value?.schuKos ?? [], 'SchuKo')}
          .userType=${'SchuKo'}
          .courses=${this._coursesFromKPF.value ?? []}
          @pool-changed=${() => this.requestUpdate()}
        ></board-component-user-pool>
        </board-component-user-pool-card>

      </cr-flex>
    `;
  }

  private _filterPoolUsers(users: ChancenwerkUser[], userType: 'US' | 'OS' | 'LeKo' | 'SchuKo'): ChancenwerkUser[] {
    return users.filter(user => this._shouldShowInPool(user, userType));
  }

  private _isV2() {
    return !!this._kpf.value?.produkt?.useV2Courses;
  }

  private _studentIsAssigned(student: Student, userType: 'US' | 'OS'): boolean {
    const idField = userType === 'US' ? 'usStudentIDs' : 'osStudentIDs';
    return (this._coursesFromKPF.value ?? []).some(c => c[idField].includes(student._id));
  }

  private _shouldShowInPool(user: ChancenwerkUser, userType: 'US' | 'OS' | 'LeKo' | 'SchuKo'): boolean {
    if (userType === 'US' || userType === 'OS') {
      return this._studentShouldShowInPool(user as Student, userType);
    }
    return !this._isUserInAllCourses(user, userType);
  }

  private _studentShouldShowInPool(student: Student, userType: 'US' | 'OS'): boolean {
    // A student who picked no Durchführungstag stays in the pool until they are placed somewhere, so
    // the coordinator still sees them (flagged in blue) instead of losing them entirely.
    if (!(student.lernfoerderungDuerchfuehrungsTagChoosen ?? []).length) {
      return !this._studentIsAssigned(student, userType);
    }

    // "Einteilung nach Raum": a child belongs to exactly one room, so one assignment takes them out
    // of the pool - otherwise they could be dropped into a second room from here.
    if (!this._isV2()) return !this._studentIsAssigned(student, userType);

    // "Einteilung nach Tag": a child may attend several days, so they stay available in the pool
    // until every day they picked in their profile is covered.
    return this._studentHasUnfulfilledCourseWishes(student, userType);
  }

  private _studentHasUnfulfilledCourseWishes(student: Student, userType: 'US' | 'OS'): boolean {
    const wishes = student.lernfoerderungDuerchfuehrungsTagChoosen ?? [];
    if (wishes.length === 0) return false;

    const idField = userType === 'US' ? 'usStudentIDs' : 'osStudentIDs';
    const courses = this._coursesFromKPF.value ?? [];
    // A wish is unfulfilled when a course exists for its slot but none of the courses
    // for that slot contain the student. In "Einteilung nach Raum" a slot can have
    // several room-courses, so we must check all of them, not just the first match.
    return wishes.some(wish => {
      const matchingCourses = courses.filter(c => courseMatchesDurchfuehrungsTag(c, wish));
      if (!matchingCourses.length) return false;
      return !matchingCourses.some(c => c[idField].includes(student._id));
    });
  }

  private _isUserInAllCourses(user: ChancenwerkUser, userType: 'LeKo' | 'SchuKo'): boolean {
    const courses = this._coursesFromKPF.value ?? [];
    if (courses.length === 0) return false;
    switch (userType) {
      case 'LeKo':
        return courses.every(c => c.leKoIDs?.includes(user._id) ?? false);
      case 'SchuKo':
        return courses.every(c => c.schuKoIDs?.includes(user._id) ?? false);
      default:
        return false;
    }
  }

  private async _saveAll() {
    for (const c of this._coursesFromKPF.value ?? []) {
      await c.save();
    }
  }
}
